import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  getAllAchievements,
  getUnlockedAchievements,
  getPendingAchievements,
  getAchievementsByType,
  getAchievementsByTier,
  getAchievementById,
  getLevelConfig,
  achievementTypeLabels,
  achievementTypeIcons,
  achievementTypeColors,
  levelConfigs,
  listenerAchievementProfile,
  achievementScoreActions,
  achievementDashboardStats,
  formatAchievementCount,
  formatAchievementDate,
  getProgressPercent,
  type Achievement,
  type AchievementType,
  type AchievementLevel,
} from '@/mocks/rfwAchievements';

type PageState = 'loading' | 'error' | 'ready';
type ViewTab = 'logros' | 'niveles' | 'perfil';

export default function LogrosPage() {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [typeFilter, setTypeFilter] = useState<AchievementType | 'todos'>('todos');
  const [tierFilter, setTierFilter] = useState<Achievement['tier'] | 'todos'>('todos');
  const [statusFilter, setStatusFilter] = useState<'todos' | 'desbloqueados' | 'pendientes'>('todos');
  const [viewTab, setViewTab] = useState<ViewTab>('logros');
  const [selectedAchievement, setSelectedAchievement] = useState<Achievement | null>(null);
  const [expandedTier, setExpandedTier] = useState<Achievement['tier'] | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setPageState('ready'), 500);
    return () => clearTimeout(t);
  }, []);

  const handleRetry = useCallback(() => {
    setPageState('loading');
    setTimeout(() => setPageState('ready'), 500);
  }, []);

  const filteredAchievements = useMemo(() => {
    let result = getAllAchievements();
    if (typeFilter !== 'todos') result = result.filter((a) => a.type === typeFilter);
    if (tierFilter !== 'todos') result = result.filter((a) => a.tier === tierFilter);
    if (statusFilter === 'desbloqueados') result = result.filter((a) => a.unlocked);
    if (statusFilter === 'pendientes') result = result.filter((a) => !a.unlocked);
    return result;
  }, [typeFilter, tierFilter, statusFilter]);

  const unlockedList = getUnlockedAchievements();
  const pendingList = getPendingAchievements();
  const currentLevelIdx = levelConfigs.findIndex((l) => l.level === listenerAchievementProfile.currentLevel);
  const nextLevelCfg = currentLevelIdx < levelConfigs.length - 1 ? levelConfigs[currentLevelIdx + 1] : null;

  const typeTabs: { id: AchievementType | 'todos'; label: string; icon: string }[] = [
    { id: 'todos', label: 'Todos', icon: 'ri-grid-line' },
    { id: 'primer_seguidor', label: 'Seguidores', icon: achievementTypeIcons.primer_seguidor },
    { id: 'primer_saludo', label: 'Saludos', icon: achievementTypeIcons.primer_saludo },
    { id: 'primera_cancion', label: 'Canciones', icon: achievementTypeIcons.primera_cancion },
    { id: 'primer_concurso', label: 'Concursos', icon: achievementTypeIcons.primer_concurso },
    { id: 'primer_evento', label: 'Eventos', icon: achievementTypeIcons.primer_evento },
    { id: 'primer_club', label: 'Clubes', icon: achievementTypeIcons.primer_club },
  ];

  const tierTabs: { id: Achievement['tier'] | 'todos'; label: string; emoji: string }[] = [
    { id: 'todos', label: 'Todos', emoji: '📋' },
    { id: 'basico', label: 'Básico', emoji: '🌟' },
    { id: 'intermedio', label: 'Intermedio', emoji: '🔥' },
    { id: 'avanzado', label: 'Avanzado', emoji: '💎' },
    { id: 'maestro', label: 'Maestro', emoji: '👑' },
  ];

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-background-50">
        <section className="relative w-full h-[280px] md:h-[380px] bg-background-200 animate-pulse" />
        <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 space-y-8">
          <div className="flex gap-2 flex-wrap">{[1,2,3,4,5,6,7].map((i) => (<div key={i} className="bg-background-100 rounded-full h-8 w-20 animate-pulse" />))}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{[1,2,3,4,5,6].map((i) => (<div key={i} className="bg-background-100 rounded-2xl h-[300px] animate-pulse" />))}</div>
        </main>
      </div>
    );
  }

  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4 py-20">
        <div className="w-20 h-20 rounded-3xl bg-red-100 flex items-center justify-center mb-5">
          <i className="ri-error-warning-line text-3xl text-red-500"></i>
        </div>
        <h2 className="text-xl font-extrabold text-foreground-950 mb-2">Error al cargar logros</h2>
        <p className="text-sm text-foreground-500 mb-6 text-center max-w-md">No pudimos cargar los datos. Verificá tu conexión e intentá de nuevo.</p>
        <button onClick={handleRetry} className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-xl text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-refresh-line"></i>Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* HERO */}
      <section className="relative w-full h-[300px] md:h-[420px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Epic%20achievement%20hall%20with%20golden%20trophies%20and%20medals%20displayed%20on%20illuminated%20pedestals%2C%20grand%20ceremonial%20atmosphere%20with%20warm%20golden%20ambient%20lighting%2C%20prestigious%20award%20ceremony%20setting%2C%20dramatic%20spotlight%20effects%20on%20achievements%2C%20museum%20quality%20display%2C%20editorial%20photography%20with%20depth%20and%20grandeur&width=1600&height=900&seq=rfw-logros-hero&orientation=landscape"
          alt="Logros RFW"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/30 to-black/75"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <div className="w-16 h-16 rounded-2xl bg-background-50/15 backdrop-blur-md flex items-center justify-center mb-4 border border-background-50/20">
            <i className="ri-trophy-line text-3xl text-background-50"></i>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-background-50 leading-tight mb-3">
            Sistema de Logros RFW
          </h1>
          <p className="text-sm md:text-base text-background-50/80 max-w-xl leading-relaxed">
            Cada hito de tu trayectoria como oyente es reconocido públicamente. Logros, niveles y score — todo visible para la comunidad.
          </p>
          <div className="flex items-center gap-3 mt-5">
            <span className="text-background-50/60 text-xs">{listenerAchievementProfile.totalAchievements} logros desbloqueados · Nivel {listenerAchievementProfile.currentLevel} · {formatAchievementCount(listenerAchievementProfile.totalScore)} pts</span>
          </div>
        </div>
      </section>

      {/* MAIN */}
      <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-12 space-y-8">
        {/* STATS STRIP */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Logros Totales', value: achievementDashboardStats.totalAchievements.toString(), icon: 'ri-star-line', color: 'text-primary-600 bg-primary-50' },
            { label: 'Oyentes con Logros', value: formatAchievementCount(achievementDashboardStats.totalListenersWithAchievements), icon: 'ri-user-star-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Logros Desbloqueados', value: formatAchievementCount(achievementDashboardStats.totalUnlockedAchievements), icon: 'ri-checkbox-circle-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Diamante', value: achievementDashboardStats.listenersDiamante.toString(), icon: 'ri-vip-crown-line', color: 'text-indigo-600 bg-indigo-50' },
            { label: 'Score Generado', value: formatAchievementCount(achievementDashboardStats.totalScoreGenerated), icon: 'ri-flashlight-line', color: 'text-violet-600 bg-violet-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-100 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* VIEW TABS */}
        <div className="flex items-center gap-1.5">
          {[
            { id: 'logros' as const, label: 'Logros', icon: 'ri-star-line', count: getAllAchievements().length },
            { id: 'niveles' as const, label: 'Niveles', icon: 'ri-stack-line', count: levelConfigs.length },
            { id: 'perfil' as const, label: 'Mi Perfil', icon: 'ri-user-line', count: 1 },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setViewTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                viewTab === tab.id ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-600 hover:text-foreground-900'
              }`}
            >
              <i className={`${tab.icon} text-[10px]`}></i>
              {tab.label}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${viewTab === tab.id ? 'bg-white/20 text-white' : 'bg-background-200 text-foreground-500'}`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* LOGROS VIEW */}
        {viewTab === 'logros' && (
          <>
            {/* Filters */}
            <div className="space-y-3">
              {/* Type filters */}
              <div className="flex flex-wrap items-center gap-1.5">
                {typeTabs.map((tab) => {
                  const isActive = typeFilter === tab.id;
                  const typeColor = tab.id !== 'todos' ? achievementTypeColors[tab.id as AchievementType] : null;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setTypeFilter(tab.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap border cursor-pointer ${
                        isActive
                          ? `border-current shadow-sm ${typeColor ? typeColor.textColor : 'text-primary-700'} ${typeColor ? typeColor.bgColor : 'bg-primary-50'}`
                          : 'border-background-200 bg-background-50 text-foreground-500 hover:text-foreground-700 hover:border-background-300'
                      }`}
                    >
                      <i className={`${tab.icon} text-[10px]`}></i>
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {/* Tier + Status filters */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex flex-wrap items-center gap-1.5">
                  {tierTabs.map((tab) => {
                    const isActive = tierFilter === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setTierFilter(tab.id)}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap border cursor-pointer ${
                          isActive
                            ? 'bg-foreground-950 text-background-50 border-foreground-950'
                            : 'border-background-200 bg-background-50 text-foreground-500 hover:text-foreground-700 hover:border-background-300'
                        }`}
                      >
                        <span className="text-[10px]">{tab.emoji}</span>
                        {tab.label}
                      </button>
                    );
                  })}
                </div>

                <div className="flex items-center gap-1.5">
                  {[
                    { id: 'todos' as const, label: 'Todos', icon: 'ri-grid-line' },
                    { id: 'desbloqueados' as const, label: 'Desbloqueados', icon: 'ri-checkbox-circle-line' },
                    { id: 'pendientes' as const, label: 'Pendientes', icon: 'ri-lock-line' },
                  ].map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setStatusFilter(s.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        statusFilter === s.id
                          ? 'bg-foreground-950 text-background-50'
                          : 'bg-background-100 text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      <i className={`${s.icon} text-[10px]`}></i>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Results count */}
            <p className="text-[11px] text-foreground-400">
              {filteredAchievements.length === 0 ? 'No se encontraron logros.' : `Mostrando ${filteredAchievements.length} logro${filteredAchievements.length !== 1 ? 's' : ''}`}
            </p>

            {/* Achievement Grid */}
            {filteredAchievements.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredAchievements.map((ach) => {
                  const progress = getProgressPercent(ach);
                  const typeColor = achievementTypeColors[ach.type];
                  return (
                    <div
                      key={ach.id}
                      onClick={() => setSelectedAchievement(ach)}
                      className={`rounded-2xl border overflow-hidden transition-all duration-300 cursor-pointer group ${
                        ach.unlocked
                          ? 'bg-background-50 border-primary-200/60 hover:border-primary-300/60'
                          : 'bg-background-100 border-background-200/70 hover:border-background-300/70'
                      }`}
                    >
                      <div className="p-5">
                        {/* Header */}
                        <div className="flex items-start justify-between mb-3">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${ach.unlocked ? ach.bgColor : 'bg-background-200'}`}>
                            {ach.unlocked ? (
                              <span className="text-xl">{ach.emoji}</span>
                            ) : (
                              <i className="ri-lock-line text-lg text-foreground-400"></i>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <span className={`px-2 py-0.5 rounded-full text-[9px] font-medium border ${
                              ach.unlocked
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : 'bg-background-200 text-foreground-500 border-background-300'
                            }`}>
                              {ach.unlocked ? 'Desbloqueado' : 'Pendiente'}
                            </span>
                            <span className={`px-1.5 py-0.5 rounded-full text-[8px] font-medium border ${typeColor.color}`}>
                              {achievementTypeLabels[ach.type]}
                            </span>
                          </div>
                        </div>

                        {/* Content */}
                        <p className="text-sm font-bold text-foreground-900 mb-1 line-clamp-1">{ach.name}</p>
                        <p className="text-[11px] text-foreground-500 leading-snug mb-3 line-clamp-2">{ach.description}</p>

                        {/* Progress */}
                        <div className="w-full bg-background-200 rounded-full h-2 mb-2">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${ach.unlocked ? 'bg-emerald-500' : 'bg-primary-500'}`}
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-foreground-500">{ach.listenerProgress}/{ach.progressRequired}</span>
                          <div className="flex items-center gap-3">
                            {ach.unlockedDate && (
                              <span className="text-[9px] text-foreground-400">{formatAchievementDate(ach.unlockedDate)}</span>
                            )}
                            <span className={`text-[11px] font-bold flex items-center gap-0.5 ${ach.unlocked ? 'text-emerald-600' : 'text-primary-600'}`}>
                              <i className="ri-flashlight-line text-[10px]"></i>+{ach.pointsReward} pts
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
                  <i className="ri-star-off-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-sm font-semibold text-foreground-400">No se encontraron logros</p>
                <p className="text-[12px] text-foreground-300 mt-1">Intentá ajustar los filtros</p>
              </div>
            )}
          </>
        )}

        {/* NIVELES VIEW */}
        {viewTab === 'niveles' && (
          <div className="space-y-8">
            {/* Level Progression */}
            <div className="rounded-3xl bg-background-100 border border-background-200/70 p-6 md:p-8">
              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-stack-line text-xl text-yellow-600"></i>
                </div>
                <h3 className="text-lg font-extrabold text-foreground-950 mb-2">Progresión de Niveles</h3>
                <p className="text-xs text-foreground-500">Bronce → Plata → Oro → Platino → Diamante. Cada nivel desbloquea nuevos beneficios.</p>
              </div>

              {/* Level ladder */}
              <div className="flex items-center justify-between gap-1 mb-8">
                {levelConfigs.map((cfg, idx) => {
                  const isAchieved = idx <= currentLevelIdx;
                  const isActive = idx === currentLevelIdx;
                  return (
                    <div key={cfg.level} className="flex-1 flex flex-col items-center gap-1">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 ${
                        isActive ? cfg.bgColor + ' ring-2 ring-offset-2 ring-primary-500 scale-110' :
                        isAchieved ? cfg.bgColor : 'bg-background-200'
                      }`}>
                        <span className="text-lg">{cfg.emoji}</span>
                      </div>
                      <p className={`text-[10px] font-bold ${isActive ? 'text-primary-700' : isAchieved ? 'text-foreground-700' : 'text-foreground-300'}`}>
                        {cfg.name}
                      </p>
                      <p className="text-[8px] text-foreground-400">{cfg.minAchievements}+ logros</p>
                      {idx < levelConfigs.length - 1 && (
                        <div className={`w-full h-0.5 rounded-full mt-1 ${isAchieved ? 'bg-emerald-300' : 'bg-background-300'}`}></div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Level detail cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {levelConfigs.map((cfg, idx) => {
                  const isAchieved = idx <= currentLevelIdx;
                  const isActive = idx === currentLevelIdx;
                  return (
                    <div
                      key={cfg.level}
                      className={`p-5 rounded-2xl border transition-all duration-200 ${
                        isActive
                          ? 'bg-background-50 border-primary-300/60 ring-1 ring-primary-200/40'
                          : isAchieved
                          ? 'bg-background-50 border-emerald-200/60'
                          : 'bg-background-100 border-background-200/70 opacity-75'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${cfg.bgColor}`}>
                          <span className="text-base">{cfg.emoji}</span>
                        </div>
                        <div>
                          <p className={`text-sm font-bold ${cfg.textColor}`}>{cfg.name}</p>
                          <p className="text-[10px] text-foreground-500">{cfg.minAchievements}+ logros · {cfg.minScore}+ pts</p>
                        </div>
                        <div className="ml-auto">
                          {isActive ? (
                            <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-[9px] font-bold">Actual</span>
                          ) : isAchieved ? (
                            <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold">Alcanzado</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full bg-background-200 text-foreground-400 text-[9px] font-medium">Bloqueado</span>
                          )}
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        {cfg.benefits.map((b, bi) => (
                          <div key={bi} className="flex items-center gap-1.5">
                            <i className={`ri-check-line text-[10px] ${isAchieved ? 'text-emerald-500' : 'text-foreground-300'}`}></i>
                            <span className="text-[10px] text-foreground-600">{b}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* PERFIL VIEW */}
        {viewTab === 'perfil' && (
          <div className="space-y-8">
            {/* Profile Card */}
            <div className="rounded-3xl bg-background-100 border border-background-200/70 p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-center gap-5 mb-8">
                <img src={listenerAchievementProfile.listenerAvatar} alt={listenerAchievementProfile.listenerName} className="w-20 h-20 rounded-full object-cover shrink-0 ring-4 ring-yellow-200" />
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-xl font-extrabold text-foreground-950">{listenerAchievementProfile.listenerName}</h3>
                    <span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 text-[10px] font-bold">
                      🥇 {levelConfigs[currentLevelIdx]?.name || 'Oro'}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-[10px] font-bold">#{listenerAchievementProfile.globalRank}</span>
                  </div>
                  <p className="text-sm text-foreground-500">{listenerAchievementProfile.totalAchievements} logros · {formatAchievementCount(listenerAchievementProfile.totalScore)} pts · Top {(listenerAchievementProfile.globalRank / listenerAchievementProfile.totalListeners * 100).toFixed(1)}%</p>
                </div>
              </div>

              {/* Progress to next level */}
              {nextLevelCfg && (
                <div className="mb-8 p-4 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{nextLevelCfg.emoji}</span>
                      <p className="text-xs font-bold text-foreground-900">Progreso a {nextLevelCfg.name}</p>
                    </div>
                    <span className="text-[10px] text-foreground-500">{listenerAchievementProfile.currentLevelProgress}%</span>
                  </div>
                  <div className="w-full bg-background-200 rounded-full h-2.5 mb-2">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-yellow-400 to-cyan-400 transition-all duration-500"
                      style={{ width: `${listenerAchievementProfile.currentLevelProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-[10px] text-foreground-500">
                    Te faltan {listenerAchievementProfile.nextLevelNeeded} puntos para alcanzar {nextLevelCfg.name}. ¡Seguí desbloqueando logros!
                  </p>
                </div>
              )}

              {/* Unlocked Achievements */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                    <i className="ri-checkbox-circle-line text-sm text-emerald-600"></i>
                  </div>
                  <p className="text-sm font-bold text-foreground-900">Logros Obtenidos</p>
                  <span className="text-[10px] text-foreground-500">{unlockedList.length}</span>
                </div>
                <div className="space-y-2">
                  {unlockedList.map((ach) => (
                    <div
                      key={ach.id}
                      onClick={() => setSelectedAchievement(ach)}
                      className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-emerald-200/60 hover:border-emerald-300/60 transition-all duration-200 cursor-pointer"
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${ach.bgColor}`}>
                        <span className="text-base">{ach.emoji}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-foreground-900">{ach.name}</p>
                        <p className="text-[10px] text-foreground-500">{ach.description}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-xs font-bold text-emerald-600">+{ach.pointsReward}</p>
                        <p className="text-[9px] text-foreground-400">{formatAchievementDate(ach.unlockedDate)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pending Achievements */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-background-200 flex items-center justify-center">
                    <i className="ri-lock-line text-sm text-foreground-500"></i>
                  </div>
                  <p className="text-sm font-bold text-foreground-900">Logros Pendientes</p>
                  <span className="text-[10px] text-foreground-500">{pendingList.length}</span>
                </div>
                <div className="space-y-2">
                  {pendingList.map((ach) => {
                    const progress = getProgressPercent(ach);
                    return (
                      <div
                        key={ach.id}
                        onClick={() => setSelectedAchievement(ach)}
                        className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-background-300/70 transition-all duration-200 cursor-pointer"
                      >
                        <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-background-200">
                          <i className="ri-lock-line text-sm text-foreground-400"></i>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-foreground-700">{ach.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex-1 bg-background-200 rounded-full h-1">
                              <div className="h-full rounded-full bg-primary-400" style={{ width: `${progress}%` }}></div>
                            </div>
                            <span className="text-[9px] text-foreground-400">{progress}%</span>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-[10px] font-semibold text-primary-500">+{ach.pointsReward}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Score Section */}
            <div className="rounded-3xl bg-background-100 border border-background-200/70 p-6 md:p-8">
              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-flashlight-line text-xl text-primary-600"></i>
                </div>
                <h3 className="text-lg font-extrabold text-foreground-950 mb-2">Score por Logros</h3>
                <p className="text-xs text-foreground-500">Cada logro desbloqueado suma puntos a tu Score RFW. Cuanto más difícil el logro, más puntos.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {achievementScoreActions.map((action, i) => (
                  <div key={i} className="p-4 rounded-xl bg-background-50 border border-background-200/70 text-center">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-2">
                      <i className={`${action.icon} text-base ${action.color}`}></i>
                    </div>
                    <p className="text-xs font-bold text-foreground-900 mb-1">{action.action}</p>
                    <p className="text-lg font-extrabold text-primary-600 mb-1">+{action.points} pts</p>
                    <p className="text-[10px] text-foreground-500 leading-snug">{action.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* CROSS LINKS */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/rankings" className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-accent-200/60 transition-all duration-200 text-center">
            <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-2">
              <i className="ri-trophy-line text-base text-accent-600"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-900">Rankings RFW</p>
            <p className="text-[10px] text-foreground-500 mt-1">Los oyentes con más logros</p>
          </Link>
          <Link to="/gamificacion" className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-amber-300/60 transition-all duration-200 text-center">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-2">
              <i className="ri-gamepad-line text-base text-amber-600"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-900">Gamificación</p>
            <p className="text-[10px] text-foreground-500 mt-1">Misiones, recompensas y más</p>
          </Link>
          <Link to="/insignias" className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-emerald-300/60 transition-all duration-200 text-center">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-2">
              <i className="ri-shield-check-line text-base text-emerald-600"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-900">Insignias RFW</p>
            <p className="text-[10px] text-foreground-500 mt-1">Todas tus insignias desbloqueadas</p>
          </Link>
        </section>

        {/* REGLA FINAL */}
        <section className="p-6 rounded-2xl bg-primary-100/70 border border-primary-200/50 text-center">
          <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-star-line text-xl text-white"></i>
          </div>
          <p className="text-lg font-extrabold text-foreground-950 mb-2">Los logros deben mostrarse públicamente.</p>
          <p className="text-sm text-foreground-600 max-w-xl mx-auto">
            Cada logro es un testimonio de tu trayectoria en RFW. No son solo puntos — son hitos que cuentan tu historia como oyente, competidor y miembro de la comunidad. Cuando otros ven tus logros, se inspiran. Cuando vos ves los suyos, aspirás a más. Así se construye una comunidad que crece junta.
          </p>
        </section>
      </main>

      {/* ─── MODAL DE DETALLE ─── */}
      {selectedAchievement && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedAchievement(null)}></div>
          <div className="relative bg-background-50 rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
            <button
              onClick={() => setSelectedAchievement(null)}
              className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-background-50/80 backdrop-blur-md flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line text-foreground-700"></i>
            </button>

            <div className="p-5 md:p-6">
              {/* Icon */}
              <div className={`w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-4 ${selectedAchievement.unlocked ? selectedAchievement.bgColor : 'bg-background-200'}`}>
                {selectedAchievement.unlocked ? (
                  <span className="text-3xl">{selectedAchievement.emoji}</span>
                ) : (
                  <i className="ri-lock-line text-2xl text-foreground-400"></i>
                )}
              </div>

              {/* Header */}
              <div className="text-center mb-4">
                <h2 className="text-lg font-extrabold text-foreground-950 mb-1">{selectedAchievement.name}</h2>
                <p className="text-sm text-foreground-500 leading-relaxed">{selectedAchievement.description}</p>
              </div>

              {/* Badges */}
              <div className="flex items-center justify-center gap-2 mb-5">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${achievementTypeColors[selectedAchievement.type].color}`}>
                  {achievementTypeLabels[selectedAchievement.type]}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${
                  selectedAchievement.unlocked
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-background-200 text-foreground-500 border-background-300'
                }`}>
                  {selectedAchievement.unlocked ? 'Desbloqueado' : 'Pendiente'}
                </span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-background-100 text-foreground-600 border border-background-200">
                  {selectedAchievement.tier === 'basico' ? '🌟 Básico' :
                   selectedAchievement.tier === 'intermedio' ? '🔥 Intermedio' :
                   selectedAchievement.tier === 'avanzado' ? '💎 Avanzado' : '👑 Maestro'}
                </span>
              </div>

              {/* Progress */}
              <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] text-foreground-500">Progreso</span>
                  <span className="text-[10px] font-bold text-foreground-700">{selectedAchievement.listenerProgress}/{selectedAchievement.progressRequired}</span>
                </div>
                <div className="w-full bg-background-200 rounded-full h-3">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${selectedAchievement.unlocked ? 'bg-emerald-500' : 'bg-primary-500'}`}
                    style={{ width: `${getProgressPercent(selectedAchievement)}%` }}
                  ></div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="text-center p-3 rounded-xl bg-background-100">
                  <p className={`text-lg font-extrabold ${selectedAchievement.unlocked ? 'text-emerald-600' : 'text-primary-600'}`}>+{selectedAchievement.pointsReward}</p>
                  <p className="text-[10px] text-foreground-500">Puntos RFW</p>
                </div>
                <div className="text-center p-3 rounded-xl bg-background-100">
                  <p className="text-lg font-extrabold text-foreground-900">{selectedAchievement.progressRequired}</p>
                  <p className="text-[10px] text-foreground-500">Requerido</p>
                </div>
                <div className="text-center p-3 rounded-xl bg-background-100">
                  <p className="text-lg font-extrabold text-foreground-900">
                    {selectedAchievement.repeatable ? 'Sí' : 'No'}
                  </p>
                  <p className="text-[10px] text-foreground-500">Repetible</p>
                </div>
              </div>

              {/* Unlock Date */}
              {selectedAchievement.unlockedDate && (
                <div className="text-center p-3 rounded-xl bg-emerald-50 border border-emerald-200 mb-5">
                  <p className="text-[10px] text-emerald-700 font-medium">
                    Desbloqueado el {formatAchievementDate(selectedAchievement.unlockedDate)}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}